import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Lock, Unlock, ShieldCheck } from "lucide-react";

export default function FrameworkLocksUser() {
  const { user_id } = useParams();
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/framework-locks/user/${user_id}`);
      setUser(data.user); setRows(data.items || []);
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user_id]);

  const setOverride = async (framework, override) => {
    setBusy(true);
    try {
      await api.put(`/admin/framework-locks/user/${user_id}`, { framework, override });
      toast.success(`${framework} → ${override === "none" ? "Use Global" : override}`);
      load();
    } catch (ex) {
      toast.error(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  if (!user) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  return (
    <div className="px-6 py-6 space-y-6" data-testid="framework-locks-user-page">
      <Link to="/admin/framework-locks" className="text-xs text-brand inline-flex items-center gap-1 hover:underline">
        <ArrowLeft className="w-3 h-3" /> Back to Framework Locks
      </Link>
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">User</div>
        <h1 className="text-3xl font-display font-bold tracking-tight mt-1">{user.name}</h1>
        <div className="text-sm text-ink-muted">{user.email}{user.company_name ? ` · ${user.company_name}` : ""}</div>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="text-left px-4 py-2">Framework</th>
              <th className="text-left px-4 py-2">Global Setting</th>
              <th className="text-left px-4 py-2">Individual Override</th>
              <th className="text-left px-4 py-2">Effective</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-ink-line bg-good-soft/30" data-testid="lock-row-TESM">
              <td className="px-4 py-3 font-display font-bold">TESM</td>
              <td className="px-4 py-3 text-good text-sm">● Always Open</td>
              <td className="px-4 py-3 text-ink-muted text-sm">—</td>
              <td className="px-4 py-3"><span className="chip border-good/40 bg-good-soft text-good text-[10px]">Open</span></td>
            </tr>
            {rows.map((r) => (
              <tr key={r.framework} className="border-t border-ink-line" data-testid={`lock-row-${r.framework}`}>
                <td className="px-4 py-3 font-display font-bold">{r.framework}</td>
                <td className="px-4 py-3 text-sm">{r.global_is_locked ? "Locked" : "Unlocked"}</td>
                <td className="px-4 py-3">
                  <select value={r.override} disabled={busy}
                    onChange={(e) => setOverride(r.framework, e.target.value)}
                    data-testid={`override-${r.framework}-select`}
                    className="px-2 py-1.5 border border-ink-line bg-white rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/20">
                    <option value="none">Use Global Setting</option>
                    <option value="unlocked">Unlocked (override)</option>
                    <option value="locked">Locked (override)</option>
                  </select>
                </td>
                <td className="px-4 py-3" data-testid={`effective-${r.framework}`}>
                  {r.effective_is_locked
                    ? <span className="chip border-warn/40 bg-warn-soft text-warn text-[10px] inline-flex items-center gap-1"><Lock className="w-3 h-3" />Locked · {r.effective_source}</span>
                    : <span className="chip border-good/40 bg-good-soft text-good text-[10px] inline-flex items-center gap-1"><Unlock className="w-3 h-3" />Unlocked · {r.effective_source}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
